import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../../../cart/CartContext';
import publicApi from '../../../services/publicApi';
import PurchaseForm from '../PurchaseForm/PurchaseForm';
import './Cart.css';

const money = value => `${new Intl.NumberFormat('ru-RU').format(value)} ₽`;

export default function Cart({ onClose }) {
    const { items, dispatch, attempt, updateAttempt } = useCart();
    const [checkout, setCheckout] = useState(Boolean(attempt));
    const [locked, setLocked] = useState(false);
    const [loading, setLoading] = useState(!attempt);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const panel = useRef(null);
    const closeRef = useRef(null);
    const latest = useRef({ onClose, locked });
    latest.current = { onClose, locked };

    useEffect(() => {
        const focused = document.activeElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();
        const onKey = event => {
            if (event.key === 'Escape' && !latest.current.locked) latest.current.onClose();
            if (event.key !== 'Tab') return;
            const controls = [...(panel.current?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])') || [])];
            const first = controls[0]; const last = controls[controls.length - 1];
            if (!first) return;
            if (event.shiftKey && (document.activeElement === first || !panel.current.contains(document.activeElement))) {
                event.preventDefault(); last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !panel.current.contains(document.activeElement))) {
                event.preventDefault(); first.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', onKey); focused?.focus?.(); };
    }, []);

    useEffect(() => {
        if (attempt) return undefined;
        let cancelled = false;
        setLoading(true); setError('');
        publicApi.catalog.getAll({ includePreorder: true }).then(products => {
            if (!cancelled) dispatch({ type: 'refresh', products });
        }).catch(() => { if (!cancelled) setError('Не удалось проверить цены и остатки. Обновите корзину перед оформлением.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [attempt, dispatch, refresh]);

    const invalid = items.some(item => item.quantity > item.available_quantity);
    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    return <div className="shop-cart-overlay" onClick={event => { if (event.target === event.currentTarget && !locked) onClose(); }}>
        <section className="shop-cart-panel" role="dialog" aria-modal="true" aria-label="Корзина" ref={panel}>
            <button ref={closeRef} type="button" className="shop-cart-close" aria-label="Закрыть корзину" onClick={onClose} disabled={locked}>✕</button>
            {checkout && items.length ? <PurchaseForm cartItems={attempt?.items || items} initialAttempt={attempt}
                onAttemptChange={updateAttempt} onBack={() => { updateAttempt(null); setCheckout(false); }} onCheckoutLockChange={setLocked} /> : <>
                <p className="section-kicker">Ваш выбор</p><h2>Корзина</h2>
                {!items.length ? <div className="shop-cart-empty"><p>Здесь будут ваши вещи.</p><p>Выберите размер в каталоге и добавьте товар в корзину.</p>
                    <button className="shop-submit-btn" onClick={onClose}>Продолжить покупки</button></div> : <>
                    <ul className="shop-cart-list">{items.map(item => <li className="shop-cart-item" key={item.variant_id}>
                        {item.image && <img src={item.image} alt={item.name} />}
                        <div className="shop-cart-description"><h3>{item.name}</h3><p>{[item.color, `Размер ${item.size}`].filter(Boolean).join(' · ')}</p>
                            <p>{money(item.price)} за шт.</p>
                            <div className="shop-cart-quantity">
                                <button type="button" aria-label={`Уменьшить количество ${item.name} ${item.size}`} disabled={item.quantity <= 1}
                                    onClick={() => dispatch({ type: 'quantity', id: item.variant_id, quantity: item.quantity - 1 })}>−</button>
                                <input type="number" inputMode="numeric" min="1" max={Math.max(1, item.available_quantity)} value={item.quantity}
                                    aria-label={`Количество ${item.name} ${item.size}`} onChange={event => dispatch({ type: 'quantity', id: item.variant_id, quantity: event.target.value })} />
                                <button type="button" aria-label={`Увеличить количество ${item.name} ${item.size}`} disabled={item.quantity >= item.available_quantity || items.reduce((sum, row) => sum + row.quantity, 0) >= 50}
                                    onClick={() => dispatch({ type: 'quantity', id: item.variant_id, quantity: item.quantity + 1 })}>+</button>
                            </div>
                            {item.quantity > item.available_quantity && <p className="shop-cart-stock" role="alert">{item.available_quantity ? `Доступно ${item.available_quantity} шт. Уменьшите количество.` : 'Размер закончился. Удалите его из корзины.'}</p>}
                        </div>
                        <div className="shop-cart-line-total"><strong>{money(item.price * item.quantity)}</strong>
                            <button type="button" aria-label={`Удалить ${item.name} ${item.size}`} onClick={() => dispatch({ type: 'remove', id: item.variant_id })}>Удалить</button></div>
                    </li>)}</ul>
                    {loading && <p role="status">Проверяем цены и остатки...</p>}
                    {error && <p role="alert">{error} <button onClick={() => setRefresh(value => value + 1)}>Обновить</button></p>}
                    <div className="shop-cart-total"><span>Стоимость товаров</span><strong>{money(total)}</strong></div>
                    <p className="shop-field-hint">Условия доставки - при оформлении заказа. Товары резервируются при переходе к оплате.</p>
                    <button className="shop-submit-btn" disabled={loading || Boolean(error) || invalid} onClick={() => setCheckout(true)}>Оформить заказ</button>
                    <button className="shop-cart-continue" onClick={onClose}>Продолжить покупки</button>
                </>}
            </>}
        </section>
    </div>;
}
